/* eslint-disable jsx-a11y/no-redundant-roles */
/* eslint-disable jsx-a11y/role-supports-aria-props */
import { HeadPhoneGuideModal } from '@/components/common/head-phone-guide-modal';
import { VoiceGuideModal } from '@/components/common/modal-voice-guide';
import { SpeakerCloseModal } from '@/components/common/speaker-close-modal';
import { ZoomDialog } from '@/components/common/zoom-dialog';
import { ClockIcon } from '@/components/icons/ClockIcon';
import { AwardLogo1 } from '@/components/icons/footer/AwardLogo1';
import { AwardLogo2 } from '@/components/icons/footer/AwardLogo2';
import FooterBackIcon from '@/components/icons/footer/BackIcon';
import { LogoIcon } from '@/components/icons/footer/LogoIcon';
import GlobalIcon from '@/components/icons/GlobalIcon';
import { useAccessibilityTTS, useDropdownTTS, useTickSoundInteraction, useVolumeButton } from '@/hooks';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useMainFocusTrapControl } from '@/hooks/useMainFocusTrapControl';
import { useOrientation } from '@/hooks/useOrientation';
import { PATHS } from '@/router/paths';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useConfigStore, useFnbStore, useShopStore, type languageType } from '@/stores';
import { CONFIG_CONSTANTS, defaultConfig, buildMenuFromBuildingMenus, getRequestStaffHelpFromBuildingMenus } from '@/stores/configStore';
import type { GNBItemType } from '@/types';
import { formatOperatingHours, getActivityTimeTTS } from '@/utils/time';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './FooterLayout.module.scss';
import { AppUtils } from '@/utils/app';
import { getLogoUrl } from '@/utils/logo';
import { NAV_KEYS } from '@/constants/keyboard';

interface FooterLayoutProps {
    onZoomButtonsOpen?: (isOpen: boolean) => void;
    isZoomButtonsOpen?: boolean;
    handleZoomClose?: () => void;
    onBarrierFreeUsed?: () => void;
    closeModal?: (closeFunction: () => void) => void;
    onBarrierFreeReset?: () => void;
}

export const FooterLayout: React.FC<FooterLayoutProps> = ({
    onZoomButtonsOpen,
    isZoomButtonsOpen,
    handleZoomClose,
    onBarrierFreeUsed,
    closeModal,
    onBarrierFreeReset,
}) => {
    // built-in hooks
    const location = useLocation();
    const navigate = useNavigate();
    const { isHorizontal } = useOrientation();
    const { t, i18n } = useTranslation();

    const ui = useConfigStore((state) => state.config.ui);
    const openTime = useConfigStore((state) => state.config.ui.openTime);
    const closeTime = useConfigStore((state) => state.config.ui.closeTime);
    const tts = useConfigStore((state) => state.config.tts);
    const voice = useConfigStore((state) => state?.config?.tts?.voice);
    const zoomIn = useConfigStore((state) => state?.config?.ui?.zoomIn);
    const contrast = useConfigStore((state) => state?.config?.ui?.contrast);
    const buildingMenus = useConfigStore((state) => state.config.server?.buildingMenus);
    const lotteEvents = useConfigStore((state) => state.config.server?.building?.lotteEvents);
    const updateConfig = useConfigStore((state) => state.updateConfig);
    const setFnbKeyboardLanguage = useFnbStore((state) => state.setKeyboardLanguage);
    const setShopKeyboardLanguage = useShopStore((state) => state.setKeyboardLanguage);
    const language = useConfigStore((state) => state.config.ui.language);
    const buildingType = useConfigStore((s) => s?.config?.server?.building?.buildingType);
    const storeLangs = useConfigStore((state) => state.config?.server?.store?.storeLangs);
    // custom hooks
    const { trapFocus, releaseFocus } = useFocusTrap();
    const { disableMainFocusTrap, enableMainFocusTrap } = useMainFocusTrapControl();
    const { speakOnFocus } = useDropdownTTS();
    const { speak } = useAccessibilityTTS();
    const { playTick } = useTickSoundInteraction();
    const { volume, handleVolumeChange, handleVolumeFocus, handleVolumeKeyDown, isVolumeChanged } = useVolumeButton(onBarrierFreeUsed);

    // local states
    const [isZoomDialogOpen, setIsZoomDialogOpen] = useState(false);
    const [isShowHeadPhoneGuide, setIsShowHeadPhoneGuide] = useState(false);
    const [isShowSpeakerClose, setIsShowSpeakerClose] = useState(false);
    const [isShowVoiceGuide, setIsShowVoiceGuide] = useState(false);
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

    // refs
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const speakTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const configTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isHomePage = location.pathname === PATHS.HOME.INDEX;

    // Get TTS parameters for activity time
    const activityTimeTTSParams = useMemo(() => getActivityTimeTTS(openTime, closeTime, language), [openTime, closeTime, language]);

    // Get appropriate TTS template based on minute availability
    const getActivityTimeTTSTemplate = useCallback(() => {
        const { hasOpenMinute, hasCloseMinute } = activityTimeTTSParams;

        if (!hasOpenMinute && !hasCloseMinute) {
            return 'footer.activity_time_tts_no_minutes';
        } else if (!hasOpenMinute) {
            return 'footer.activity_time_tts_no_open_minute';
        } else if (!hasCloseMinute) {
            return 'footer.activity_time_tts_no_close_minute';
        } else {
            return 'footer.activity_time_tts';
        }
    }, [activityTimeTTSParams]);

    // Memoized handlers
    const handleConfigUpdate = useCallback(
        (updates: Partial<typeof ui>) => {
            updateConfig({ ui: { ...ui, ...updates } });
        },
        [ui, updateConfig]
    );

    const handleContrastToggle = useCallback(() => {
        handleConfigUpdate({ contrast: !ui.contrast });
        onBarrierFreeUsed?.();
    }, [ui.contrast, handleConfigUpdate]);

    const handleScreenModeToggle = useCallback(() => {
        const newMode = ui.screenMode === 'fullscreen' ? 'split' : 'fullscreen';
        handleConfigUpdate({ screenMode: newMode });
        onBarrierFreeUsed?.();
    }, [ui.screenMode, handleConfigUpdate, onBarrierFreeUsed]);

    const handleZoomToggle = useCallback(() => {
        if (isZoomButtonsOpen) {
            handleZoomClose?.();
            return;
        }
        setIsZoomDialogOpen(true);
        onBarrierFreeUsed?.();
        TrackingService.onHomePopupOpen();
    }, [isZoomButtonsOpen, handleZoomClose, onBarrierFreeUsed]);

    const handleVoiceChange = useCallback(() => {
        if (tts.voice) {
            if (tts.speaker === 'headphone') {
                setIsShowHeadPhoneGuide(true);
            } else {
                setIsShowSpeakerClose(true);
            }
        } else {
            if (!tts.voice) {
                updateConfig({ tts: { ...tts, voice: true } });
                setIsShowVoiceGuide(true);
                onBarrierFreeUsed?.();
            }
        }
    }, [tts, onBarrierFreeUsed]);

    const handleOnConfirmSpeakerCloseModal = useCallback(() => {
        setIsShowSpeakerClose(false);
        const currentLanguage = (storeLangs?.find((lang) => lang.main_lang)?.code as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;
        // Get menu items using buildMenuFromBuildingMenus
        const menu: GNBItemType[] = buildMenuFromBuildingMenus(buildingMenus, currentLanguage, lotteEvents);

        // Change i18n language to Korean
        i18n.changeLanguage(currentLanguage);

        updateConfig({
            ui: {
                ...ui,
                language: currentLanguage,
                menu,
                zoomIn: false,
                zoomClosed: true,
                contrast: false,
                volume: 3,
                screenMode: 'fullscreen',
                zoomLevel: 1,
                requestStaffHelp: getRequestStaffHelpFromBuildingMenus(buildingMenus, currentLanguage),
            },
            tts: {
                ...tts,
                voice: false,
            },
        });

        // Reset keyboard language to Korean
        setFnbKeyboardLanguage(currentLanguage === 'ko' ? 'ko' : 'en');
        setShopKeyboardLanguage(currentLanguage === 'ko' ? 'ko' : 'en');

        onZoomButtonsOpen?.(false);
        navigate(PATHS.HOME.INDEX);
    }, [tts, ui, buildingMenus, lotteEvents, i18n, updateConfig, navigate, onZoomButtonsOpen, setFnbKeyboardLanguage, setShopKeyboardLanguage, storeLangs]);

    const handleOnConfirmVoiceGuideModal = useCallback(() => {
        setIsShowVoiceGuide(false);
        updateConfig({ tts: { ...tts, voice: true } });
        navigate(PATHS.HOME.INDEX);
    }, [tts, updateConfig, navigate]);

    const handleOnCloseVoiceGuideModal = () => {
        setIsShowVoiceGuide(false);
        updateConfig({ tts: { ...tts, voice: false } });
    };
    const handleOnConfirmAndNotRedirectHome = () => {
        setIsShowVoiceGuide(false);
        updateConfig({ tts: { ...tts, voice: true } });
    };

    // Custom TTS for language selection
    const speakLanguageSelection = useCallback(
        (languageLabel: string, lang: languageType) => {
            if (!voice) return;
            // Use a custom message for language selection
            const ttsText = t('footer.barrier_free.language_selected', { language: languageLabel });
            speak(ttsText, lang);
        },
        [voice, speak, t]
    );

    const handleLanguageSelect = useCallback(
        (langValue: string, optionLabel: string) => {
            // Clear any existing timeouts
            if (speakTimeoutRef.current) {
                clearTimeout(speakTimeoutRef.current);
            }
            if (configTimeoutRef.current) {
                clearTimeout(configTimeoutRef.current);
            }

            i18n.changeLanguage(langValue);

            // Change i18n language first
            onBarrierFreeUsed?.();

            onZoomButtonsOpen?.(false);
            TrackingService.onLocaleChanged();

            setIsLanguageDropdownOpen(false);

            if (langValue === 'ko') {
                setFnbKeyboardLanguage('ko');
                setShopKeyboardLanguage('ko');
            } else {
                setFnbKeyboardLanguage('en');
                setShopKeyboardLanguage('en');
            }

            speakTimeoutRef.current = setTimeout(() => {
                speakLanguageSelection(optionLabel, langValue as languageType);
            }, 50);

            // Get menu items using buildMenuFromBuildingMenus
            const menu: GNBItemType[] = buildMenuFromBuildingMenus(buildingMenus, langValue, lotteEvents);

            configTimeoutRef.current = setTimeout(() => {
                handleConfigUpdate({
                    language: langValue as 'ko' | 'en' | 'ja' | 'zh-CN' | 'zh-TW',
                    menu,
                    zoomIn: false,
                    zoomClosed: true,
                    contrast: false,
                    screenMode: 'fullscreen',
                    volume: 3,
                    zoomLevel: 1,
                    requestStaffHelp: getRequestStaffHelpFromBuildingMenus(buildingMenus, langValue),
                });
                navigate(PATHS.HOME.INDEX);
            }, 1200);
        },
        [buildingMenus, lotteEvents, handleConfigUpdate, navigate, speakLanguageSelection, onZoomButtonsOpen, i18n, updateConfig]
    );

    const handleToggleDropdown = useCallback(() => {
        if (!isLanguageDropdownOpen) {
            setIsLanguageDropdownOpen(true);
        } else {
            setIsLanguageDropdownOpen(false);
        }
    }, [isLanguageDropdownOpen, releaseFocus]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (!isLanguageDropdownOpen) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsLanguageDropdownOpen(true);
                }
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                setIsLanguageDropdownOpen(false);
                releaseFocus();
            }
        },
        [isLanguageDropdownOpen, releaseFocus]
    );

    const handleZoomDialogClose = () => {
        setIsZoomDialogOpen(false);
        onZoomButtonsOpen?.(false);
        TrackingService.onHomePopupClose();
        onBarrierFreeReset?.();
    };

    const handleZoomDialogConfirm = () => {
        onBarrierFreeUsed?.();
        handleConfigUpdate({ zoomIn: !zoomIn, zoomClosed: false, zoomLevel: 2 });
        setIsZoomDialogOpen(false);
        onZoomButtonsOpen?.(true);
        TrackingService.onHomePopupClose();
    };

    // Function to close all modals - used by CountdownButton
    const handleCloseAllModals = useCallback(() => {
        setIsZoomDialogOpen(false);
        setIsShowHeadPhoneGuide(false);
        setIsShowSpeakerClose(false);
        setIsShowVoiceGuide(false);
        setIsLanguageDropdownOpen(false);
    }, []);

    // Expose closeModal function to parent component
    useEffect(() => {
        if (closeModal) {
            closeModal(handleCloseAllModals);
        }
    }, [closeModal, handleCloseAllModals]);

    // Close dropdown when clicking outside and handle focus
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsLanguageDropdownOpen(false);
                releaseFocus();
            }
        };

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isLanguageDropdownOpen) {
                setIsLanguageDropdownOpen(false);
                releaseFocus();
            }
        };

        if (isLanguageDropdownOpen) {
            // Focus first option
            const firstOptionButton = dropdownRef.current?.querySelector('button[role="button"]') as HTMLButtonElement;
            if (firstOptionButton) {
                firstOptionButton.focus();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isLanguageDropdownOpen, releaseFocus]);

    useEffect(() => {
        if (isLanguageDropdownOpen && dropdownRef.current) {
            disableMainFocusTrap();
            trapFocus(dropdownRef.current, isLanguageDropdownOpen);
        } else {
            releaseFocus();
        }
    }, [isLanguageDropdownOpen, trapFocus, releaseFocus, disableMainFocusTrap, enableMainFocusTrap]);

    const LogoSection = useMemo(() => {
        if (!isHorizontal) return null;
        const logoUrl = getLogoUrl(isHorizontal, buildingType, contrast);
        return <div className={styles['footer__info__logo']}>{logoUrl ? <img src={logoUrl} alt="Logo" /> : <LogoIcon />}</div>;
    }, [isHorizontal, buildingType, contrast]);

    const AwardSection = useMemo(() => {
        if (!isHorizontal) return null;
        return (
            <div className={styles['footer__actions__award']}>
                <AwardLogo1 />
                <AwardLogo2 />
            </div>
        );
    }, [isHorizontal]);

    const AccessibilityButtons = useMemo(() => {
        return (
            <div className="flex items-center gap-[calc(8*var(--vw-unit))]">
                {ui.barrierFree && (
                    <button
                        type="button"
                        role="switch"
                        aria-checked={tts?.voice !== defaultConfig.tts.voice}
                        aria-valuetext={t('footer.barrier_free.voice_tts')}
                        data-playsound="ting"
                        data-valuenow={tts?.voice}
                        data-interactive
                        onClick={handleVoiceChange}
                        className={styles['footer__actions__button']}
                    >
                        <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.voice')}</span>
                    </button>
                )}

                <button
                    data-tts-scope="volume-button"
                    type="button"
                    role="switch"
                    data-role={t('common.button.tts.toggle_button')}
                    aria-checked={isVolumeChanged}
                    aria-label={t('footer.barrier_free.volume')}
                    data-valuenow={volume}
                    data-volumn-level={volume}
                    data-interactive
                    onClick={handleVolumeChange}
                    onFocus={handleVolumeFocus}
                    onKeyDown={handleVolumeKeyDown}
                    className={styles['footer__actions__button']}
                >
                    <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.volume') + ` ${volume}`}</span>
                </button>

                <button
                    type="button"
                    role="switch"
                    aria-checked={ui.contrast}
                    data-valuenow={ui.contrast}
                    aria-valuetext={t('footer.barrier_free.contrast_tts')}
                    data-interactive
                    onClick={handleContrastToggle}
                    className={styles['footer__actions__button']}
                >
                    <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.contrast')}</span>
                </button>

                <button
                    type="button"
                    role="switch"
                    data-role={t('common.button.tts.switch_button')}
                    aria-checked={ui.zoomIn}
                    aria-valuetext={t('footer.barrier_free.zoom_in_tts')}
                    data-interactive
                    data-playsound="ting"
                    onClick={handleZoomToggle}
                    className={styles['footer__actions__button']}
                >
                    <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.zoom_in')}</span>
                </button>

                <button
                    type="button"
                    role="switch"
                    aria-checked={ui.screenMode !== 'fullscreen'}
                    aria-valuetext={t('footer.barrier_free.screen_mode_tts')}
                    data-interactive
                    onClick={handleScreenModeToggle}
                    className={styles['footer__actions__button']}
                >
                    <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.screen_mode')}</span>
                </button>
            </div>
        );
    }, [
        ui.barrierFree,
        ui.contrast,
        ui.screenMode,
        ui.zoomIn,
        volume,
        tts,
        t,
        handleContrastToggle,
        handleScreenModeToggle,
        handleZoomToggle,
        handleVolumeChange,
        handleVoiceChange,
    ]);

    return (
        <footer className={styles['footer']} role="contentinfo" data-nav="footer">
            <div className={styles['footer__content']}>
                {isHomePage ? (
                    <div className={styles['footer__info']}>
                        {LogoSection}
                        <div
                            role="list"
                            tabIndex={0}
                            className={styles['footer__info__open'] + ' focus-border'}
                            data-region-nav
                            aria-label={`${t('footer.activity_time')} ${t(getActivityTimeTTSTemplate(), activityTimeTTSParams)}`}
                            data-interactive
                        >
                            <ClockIcon />
                            <span className={styles['footer__info__open__time']}>
                                {t('footer.activity_time')}
                                <span className={clsx(styles['footer__info__open__time--hour'], 'number')}>{formatOperatingHours(openTime, closeTime)}</span>
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className={styles['footer__actions']}>
                        <button
                            ref={buttonRef}
                            data-region-nav
                            type="button"
                            className={clsx(styles['footer__actions__button'], styles['back'])}
                            onClick={() => {
                                playTick();
                                navigate(-1);
                            }}
                            onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                                if (NAV_KEYS.includes(e.key)) {
                                    speak(t('common.data_role.screen_back'));
                                }
                            }}
                        >
                            <FooterBackIcon />
                            <span className={styles['footer__actions__button__label']}>{t('footer.back')}</span>
                        </button>
                    </div>
                )}
                <div className={styles['footer__actions']} data-region-nav>
                    {AwardSection}
                    {AccessibilityButtons}
                    <div className={styles.language} data-tts-scope="dropdown" ref={dropdownRef}>
                        <button
                            ref={buttonRef}
                            type="button"
                            data-role={t('common.data_role.button')}
                            className={clsx(styles['footer__actions__button'])}
                            aria-label={t('footer.barrier_free.language')}
                            aria-expanded={isLanguageDropdownOpen}
                            data-interactive
                            onClick={handleToggleDropdown}
                            onKeyDown={handleKeyDown}
                            onFocus={(e) => {
                                speakOnFocus(e.target);
                            }}
                        >
                            <GlobalIcon />
                            <span className={clsx('font-number', styles['footer__actions__button__label'])}>{t('footer.barrier_free.language_label')}</span>
                        </button>

                        {isLanguageDropdownOpen && (
                            <div className={styles['language__options']} role="tablist" aria-label={t('footer.barrier_free.language')}>
                                {ui.languageOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        role="button"
                                        data-tts-scope="dropdown"
                                        aria-label={AppUtils.getLanguageLabelByActiveLang(option.value as languageType, language)}
                                        aria-checked={option.value === ui.language}
                                        aria-selected={option.value === ui.language}
                                        className={clsx(styles['language__options__item'], { [styles.selected]: option.value === ui.language })}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleLanguageSelect(option.value, option.label);
                                        }}
                                        style={{ fontFamily: AppUtils.getLanguageFont(option.value, buildingType) }}
                                        onFocus={(e) => {
                                            speakOnFocus(e.target);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleLanguageSelect(option.value, option.label);
                                            }
                                        }}
                                        data-interactive
                                        tabIndex={0}
                                        disabled={!isLanguageDropdownOpen}
                                    >
                                        <span className={option.value === 'en' ? 'font-number' : ''}>{option.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ZoomDialog isOpen={isZoomDialogOpen} onClose={handleZoomDialogClose} onConfirm={handleZoomDialogConfirm} />
            <SpeakerCloseModal isOpen={isShowSpeakerClose} onClose={() => setIsShowSpeakerClose(false)} onConfirm={handleOnConfirmSpeakerCloseModal} />
            <HeadPhoneGuideModal isOpen={isShowHeadPhoneGuide} onClose={() => setIsShowHeadPhoneGuide(false)} />
            <VoiceGuideModal
                isOpen={isShowVoiceGuide}
                onClose={handleOnCloseVoiceGuideModal}
                onConfirm={handleOnConfirmVoiceGuideModal}
                onConfirmAndNotRedirectHome={handleOnConfirmAndNotRedirectHome}
            />
        </footer>
    );
};

export default FooterLayout;
